import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';

@Injectable()
export class RateLimitInterceptor implements HttpInterceptor {
  private requestCounts = new Map<string, { count: number; resetTime: number }>();
  private readonly MAX_REQUESTS_PER_SECOND = 3;
  private readonly WINDOW_MS = 1000;
  private readonly EXCLUDED_ENDPOINTS = [
    '/api/v1/token',
    '/api/v1/customer/dashboard',
    // Bulk-write endpoints legitimately bursted by copy/template flows.
    '/task-lists/',
    '/address-task-groups',
    '/photo-notes',
    '/text-notes',
  ];

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.EXCLUDED_ENDPOINTS.some(endpoint => req.url.includes(endpoint))) {
      return next.handle(req);
    }

    const key = this.keyFor(req);
    const now = Date.now();
    const entry = this.requestCounts.get(key);
    if (entry && now < entry.resetTime) {
      entry.count++;
      if (entry.count > this.MAX_REQUESTS_PER_SECOND) {
        // The key can hold the request body; log only the method and URL (the console reaches Sentry).
        console.warn(`[RateLimitInterceptor] Rate limit exceeded for ${req.method}:${req.urlWithParams} (${entry.count} requests in window)`);
        return throwError(() => new Error(`Rate limit exceeded for ${req.url}`));
      }
    } else {
      this.requestCounts.set(key, { count: 1, resetTime: now + this.WINDOW_MS });
    }
    if (this.requestCounts.size > 1000) {
      this.cleanup(now);
    }
    return next.handle(req);
  }

  // Writes to one endpoint with different bodies are different requests (a save that
  // stores several metadata keys posts customer/metadata once per key), so only a
  // repeat of the same write counts towards the limit. A body that can't be
  // serialized falls back to the method and URL alone.
  private keyFor(req: HttpRequest<any>): string {
    const key = `${req.method}:${req.urlWithParams}`;
    if (req.method === 'GET' || req.method === 'HEAD' || req.body === null || req.body === undefined) {
      return key;
    }
    try {
      return `${key}:${typeof req.body === 'string' ? req.body : JSON.stringify(req.body)}`;
    } catch {
      return key;
    }
  }

  private cleanup(now: number): void {
    for (const [key, entry] of this.requestCounts.entries()) {
      if (now >= entry.resetTime) {
        this.requestCounts.delete(key);
      }
    }
  }
}
